import React from "react";
import { Link } from "react-router-dom";
import { Plus, Star, Heart, GitCompareArrows } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCompare } from "@/context/CompareContext";
import { useLang } from "@/context/LanguageContext";
import { fmtPrice } from "@/lib/api";
import LandFingerprint from "@/components/LandFingerprint";

function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const { user, isFavorite, toggleFavorite } = useAuth();
  const { has, toggle } = useCompare();
  const { t, L } = useLang();
  const tags = product.tags || [];
  const fav = isFavorite(product.sku);
  const inCompare = has(product.sku);
  const name = L(product, "name");

  return (
    <article
      data-testid={`product-card-${product.sku}`}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-black/5 bg-white transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-24px_rgba(44,62,45,0.35)]"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <Link to={`/product/${product.slug}`} className="relative block aspect-[4/5] overflow-hidden bg-sand">
        <img
          src={product.images?.main}
          alt={name}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
          <div className="flex flex-col gap-1.5">
            {tags.includes("bestseller") && (
              <span className="rounded-full bg-palred px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">{t("bestseller")}</span>
            )}
            {tags.includes("new") && (
              <span className="rounded-full bg-gold px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">{t("new")}</span>
            )}
            {product.organic && (
              <span className="rounded-full bg-olive px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">{t("organic")}</span>
            )}
          </div>
          <LandFingerprint fingerprint={product.fingerprint} compact />
        </div>
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-3">
          {product.is_demo ? (
            <span className="rounded-md bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur">تجريبي</span>
          ) : <span />}
          <div className="flex gap-1.5">
            <button
              data-testid={`compare-toggle-${product.sku}`}
              onClick={(e) => { e.preventDefault(); toggle(product.sku, product.name_ar); }}
              className={`flex h-9 w-9 items-center justify-center rounded-full backdrop-blur transition-colors ${inCompare ? "bg-olive text-white" : "bg-white/85 text-ink/70 hover:text-olive"}`}
              aria-label="أضف للمقارنة"
            >
              <GitCompareArrows className="h-4 w-4" />
            </button>
            {user && (
              <button
                data-testid={`favorite-toggle-${product.sku}`}
                onClick={(e) => { e.preventDefault(); toggleFavorite(product.sku); }}
                className={`flex h-9 w-9 items-center justify-center rounded-full backdrop-blur transition-colors ${fav ? "bg-palred text-white" : "bg-white/85 text-ink/70 hover:text-palred"}`}
                aria-label="أضف للمفضلة"
              >
                <Heart className={`h-4 w-4 ${fav ? "fill-current" : ""}`} />
              </button>
            )}
          </div>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <Link to={`/product/${product.slug}`} className="link-underline mb-1 inline-block">
          <h3 className="font-display text-base font-semibold text-ink transition-colors group-hover:text-olive">{name}</h3>
        </Link>
        <p className="mb-3 line-clamp-2 text-sm text-ink/55">{product.short_story || product.origin_text}</p>

        <div className="mt-auto flex items-center justify-between gap-2">
          <div>
            <div className="font-display text-lg font-bold text-olive-dark">{fmtPrice(product.price)}</div>
            {product.rating > 0 && (
              <div className="flex items-center gap-1 text-xs text-ink/50">
                <Star className="h-3.5 w-3.5 fill-gold text-gold" />
                <span>{product.rating}</span>
                <span>({product.reviews_count})</span>
              </div>
            )}
          </div>
          <button
            data-testid={`add-to-cart-${product.sku}`}
            onClick={() => add(product)}
            className="btn-shine flex h-11 w-11 items-center justify-center rounded-full bg-olive text-white transition-[background-color,transform] duration-300 hover:bg-olive-dark active:scale-90"
            aria-label="أضف إلى السلة"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div
        className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ boxShadow: "inset 0 0 0 1.5px rgba(184,153,71,0.5), 0 0 28px -6px rgba(184,153,71,0.35)" }}
        aria-hidden="true"
      />
    </article>
  );
}

export default React.memo(ProductCard);
